import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import { UserStore } from 'src/app/modules/authentication/state/user.store';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Clipboard } from '@capacitor/clipboard';

@Component({
    selector: 'app-number-otp',
    templateUrl: './number-otp.component.html',
    styleUrls: ['./number-otp.component.scss'],
    standalone: false
})
export class NumberOtpComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  @Input() labelText = 'Verification Code';
  @Input() showResendCode = true;
  @Output() otpResponse = new EventEmitter<{ otp?: any; resendOtp?: boolean, via?: number }>();

  // Single maxlength="6" input, drawn as six boxes via CSS/otpBoxes - see number-otp.component.html.
  // A six-separate-input layout can't be filled by iOS/Android's autofill inside the Capacitor
  // WebView: distributing an autofilled code across sibling maxlength="1" fields is a Safari-chrome
  // trick, not something WKWebView implements, so it only ever fills the one focused box.
  @ViewChild('otpInput') otpInputRef?: ElementRef<HTMLInputElement>;

  person: any;
  mask = '';

  otpForm = new FormGroup({
    otp: new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)])
  });

  // Guards against re-filling from a stale clipboard value and against clobbering
  // an in-progress manual entry once the user has started typing.
  private clipboardAutoFillAttempted = false;
  private appResumeListener?: PluginListenerHandle;

  constructor(private ref: ChangeDetectorRef, private userStore: UserStore) { }

  ngOnInit(): void {
    this.person = this.userStore.getValue();
    let m = this.person.person_email.split('@');
    this.mask = `${m[0].substring(0,2)}***@***${m[1].substring(m[1].length,5)}`;
  }

  ngAfterViewInit(): void {
    this.ref.detectChanges();

    // The static `autofocus` attribute frequently loses the race against Angular's
    // route/animation lifecycle (this screen's parent slides in over ~900ms), so it
    // silently no-ops. Focusing explicitly once the view has settled is more reliable.
    setTimeout(() => this.otpInputRef?.nativeElement.focus(), 0);

    // Native app only: web SMS/one-time-code autofill (autocomplete="one-time-code")
    // already works via the browser and doesn't need this. Inside the Capacitor
    // WebView neither iOS Security Code AutoFill nor Android's WebOTP are available,
    // so fall back to reading the code from the clipboard whenever the app comes to
    // the foreground (Android only allows clipboard reads while in the foreground).
    if (Capacitor.isNativePlatform()) {
      this.tryFillFromClipboard();
      App.addListener('resume', () => this.tryFillFromClipboard()).then(handle => {
        this.appResumeListener = handle;
      });
    }
  }

  ngOnDestroy(): void {
    this.appResumeListener?.remove();
  }

  private async tryFillFromClipboard(): Promise<void> {
    if (this.clipboardAutoFillAttempted || this.otpForm?.dirty) {
      return;
    }

    try {
      const { value } = await Clipboard.read();
      const digits = (value || '').replace(/\D/g, '');

      if (digits.length === 6) {
        this.clipboardAutoFillAttempted = true;
        this.fillDigits(digits);
      }
    } catch {
      // No clipboard permission / nothing to read - fall back to manual entry.
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.showResendCode?.currentValue !== changes?.showResendCode?.previousValue) {
      this.showResendCode = changes?.showResendCode?.currentValue;
    }
    if (changes?.labelText?.currentValue !== changes?.labelText?.previousValue) {
      this.labelText = changes?.labelText?.currentValue;
    }
  }

  resendOtp(o: number): void {
    console.log(o)
    this.otpResponse.emit({resendOtp: true, via: o});
    this.otpForm?.reset();
  }

  // Six placeholder characters for the visual boxes, derived from the single control's value.
  get otpBoxes(): string[] {
    const value = this.otpForm.get('otp')?.value || '';
    return Array.from({ length: 6 }, (_, i) => value[i] || '');
  }

  get otpLength(): number {
    return (this.otpForm.get('otp')?.value || '').length;
  }

  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 6);
    if (input.value !== digits) {
      input.value = digits;
    }
    this.otpForm.get('otp')?.setValue(digits);
    this.checkComplete();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      this.clipboardAutoFillAttempted = true;
      this.fillDigits(digits);
    }
  }

  private fillDigits(digits: string): void {
    const input = this.otpInputRef?.nativeElement;
    if (input) {
      input.value = digits;
    }
    this.otpForm.get('otp')?.setValue(digits);
    this.checkComplete();
  }

  private checkComplete(): void {
    const otp = this.otpForm.get('otp')?.value || '';
    if (otp.length === 6) {
      this.otpResponse.emit({ otp });
    }
  }
}
